import { MessageSquare, Plus, Trash2, Edit3, LogOut, Search, X } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import logo from '../images/logo.svg';
import './Sidebar.css';
import TenantSelector from './TenantSelector';
import ProjectSelector from './ProjectSelector';
import { groupThreadsByTime, getGroupLabel, getNonEmptyGroups, getTenantColor, type Thread, type GroupedThreads } from '../utils/timeGrouping';
import { useTenant } from '../contexts/TenantContext';
import { useProject } from '../contexts/ProjectContext';

interface SidebarProps {
  isOpen: boolean;
  threadId: string;
  onNewThread: () => void;
  onSwitchThread?: (threadId: string) => void;
  onUpdateThreadTitle?: (threadId: string, title: string) => void;
  onLogout?: () => void;
}

function Sidebar({ isOpen, threadId, onNewThread, onSwitchThread, onUpdateThreadTitle, onLogout }: SidebarProps) {
  const { currentTenant, switchTenant, availableTenants, isSingleTenant } = useTenant();
  const { switchProject } = useProject();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);

  // Load threads from localStorage on mount
  useEffect(() => {
    const savedThreads = localStorage.getItem('chatThreads');
    if (savedThreads) {
      const parsedThreads = JSON.parse(savedThreads).map((thread: any) => ({
        ...thread,
        timestamp: new Date(thread.timestamp)
      }));
      setThreads(parsedThreads);
    }
  }, []);

  // Save threads to localStorage when threads change
  // Smart merge: don't overwrite good titles with "New Chat"
  useEffect(() => {
    // Don't save empty array (prevents clearing localStorage on mount)
    if (threads.length === 0) {
      return;
    }

    // Read current localStorage to check for better titles
    const savedThreads = localStorage.getItem('chatThreads');
    const existingThreads: Thread[] = savedThreads
      ? JSON.parse(savedThreads).map((t: any) => ({
          ...t,
          timestamp: new Date(t.timestamp)
        }))
      : [];

    // Merge: prefer data from localStorage over state
    // This prevents race condition where Sidebar overwrites data saved by App.tsx
    let hasUpdates = false;
    const mergedThreads = threads.map(thread => {
      const existing = existingThreads.find(t => t.id === thread.id);
      if (existing) {
        // Preserve title if localStorage has real title and we have "New Chat"
        const shouldPreserveTitle = thread.title === 'New Chat' && existing.title !== 'New Chat';
        // Preserve tenant/project context from localStorage (App.tsx saves this)
        const shouldPreserveContext = !thread.tenantId && existing.tenantId;

        if (shouldPreserveTitle || shouldPreserveContext) {
          hasUpdates = true;
          return {
            ...thread,
            title: shouldPreserveTitle ? existing.title : thread.title,
            // Always preserve tenant/project from localStorage if available
            tenantId: existing.tenantId || thread.tenantId,
            tenantName: existing.tenantName || thread.tenantName,
            projectUuid: existing.projectUuid || thread.projectUuid,
            projectName: existing.projectName || thread.projectName,
          };
        }
      }
      return thread;
    });

    localStorage.setItem('chatThreads', JSON.stringify(mergedThreads));

    // If we found better titles in localStorage, update state to reflect them in UI
    if (hasUpdates) {
      setThreads(mergedThreads);
    }
  }, [threads]);

  // Add or update current thread
  useEffect(() => {
    if (threadId) {
      setThreads(prev => {
        const existingIndex = prev.findIndex(t => t.id === threadId);

        if (existingIndex >= 0) {
          // Update existing thread timestamp
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], timestamp: new Date() };
          return updated;
        } else {
          // Add new thread with placeholder title (will be updated by title generation)
          const threadData: Thread = {
            id: threadId,
            title: 'New Chat',
            timestamp: new Date()
          };
          return [threadData, ...prev];
        }
      });
    }
  }, [threadId]);

  // Listen for title updates from parent
  useEffect(() => {
    // This effect syncs title updates back to the threads list
    const handleStorageChange = () => {
      const savedThreads = localStorage.getItem('chatThreads');
      if (savedThreads) {
        const parsedThreads = JSON.parse(savedThreads).map((thread: any) => ({
          ...thread,
          timestamp: new Date(thread.timestamp)
        }));
        setThreads(parsedThreads);
      }
    };

    // Listen for cross-tab storage changes
    window.addEventListener('storage', handleStorageChange);
    // Listen for same-window custom event (storage event only fires cross-tab)
    window.addEventListener('chatThreadsUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('chatThreadsUpdated', handleStorageChange);
    };
  }, []);

  // Group threads by time
  const groupedThreads: GroupedThreads = useMemo(() => {
    let filteredThreads = threads;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filteredThreads = threads.filter(thread =>
        thread.title.toLowerCase().includes(query)
      );
    }

    return groupThreadsByTime(filteredThreads);
  }, [threads, searchQuery]);

  const nonEmptyGroups = useMemo(() => getNonEmptyGroups(groupedThreads), [groupedThreads]);

  const handleSwitchThread = (id: string) => {
    if (onSwitchThread && editingThreadId !== id) {
      // Find the thread to get its tenant/project context
      const thread = threads.find(t => t.id === id);

      // Auto-switch tenant if different from current
      if (thread?.tenantId) {
        const targetTenant = availableTenants.find(t => t.id === thread.tenantId);
        if (targetTenant) {
          switchTenant(thread.tenantId);
        }
      }

      // Auto-switch project if different from current (after tenant switch)
      // Note: This needs a slight delay since project list depends on tenant
      if (thread?.projectUuid) {
        setTimeout(() => {
          switchProject(thread.projectUuid!);
        }, 100);
      } else if (thread?.tenantId) {
        // If thread has tenant but no project, switch to "All Projects"
        setTimeout(() => {
          switchProject(null);
        }, 100);
      }

      onSwitchThread(id);
    }
  };

  const handleDeleteThread = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setThreads(prev => prev.filter(thread => thread.id !== id));

    // Also delete messages from localStorage
    localStorage.removeItem(`thread_messages_${id}`);

    // If we're deleting the current thread, start a new one
    if (id === threadId) {
      onNewThread();
    }
  };

  const handleStartEditing = (thread: Thread, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingThreadId(thread.id);
    setEditingTitle(thread.title);
  };

  const handleSaveTitle = (threadId: string) => {
    const trimmedTitle = editingTitle.trim();
    if (trimmedTitle && trimmedTitle !== '') {
      setThreads(prev => prev.map(thread =>
        thread.id === threadId
          ? { ...thread, title: trimmedTitle }
          : thread
      ));

      if (onUpdateThreadTitle) {
        onUpdateThreadTitle(threadId, trimmedTitle);
      }
    }
    setEditingThreadId(null);
    setEditingTitle('');
  };

  const handleCancelEditing = () => {
    setEditingThreadId(null);
    setEditingTitle('');
  };

  const handleKeyPress = (e: React.KeyboardEvent, threadId: string) => {
    if (e.key === 'Enter') {
      handleSaveTitle(threadId);
    } else if (e.key === 'Escape') {
      handleCancelEditing();
    }
  };

  const handleClearAllThreads = () => {
    // Clear all thread messages from localStorage
    threads.forEach(thread => {
      localStorage.removeItem(`thread_messages_${thread.id}`);
    });
    setThreads([]);
    onNewThread();
  };

  // Build tooltip text for thread
  const getThreadTooltip = (thread: Thread): string | null => {
    if (!thread.tenantName) return null;
    if (thread.projectName) {
      return `${thread.tenantName} • ${thread.projectName}`;
    }
    return thread.tenantName;
  };

  // Get first message preview for threads that still have "New Chat" title
  const getFirstMessagePreview = (threadId: string): string | null => {
    try {
      const messages = localStorage.getItem(`thread_messages_${threadId}`);
      if (messages) {
        const parsed = JSON.parse(messages);
        const firstUserMsg = parsed.find((m: { role: string; content: string }) => m.role === 'user');
        if (firstUserMsg && firstUserMsg.content) {
          const content = firstUserMsg.content.trim();
          if (content.length <= 30) return content;
          // Cut at word boundary for cleaner display
          const truncated = content.substring(0, 30);
          const lastSpace = truncated.lastIndexOf(' ');
          return (lastSpace > 15 ? truncated.substring(0, lastSpace) : truncated) + '...';
        }
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  };

  // Get display title - use first message preview if title is "New Chat"
  const getDisplayTitle = (thread: Thread): string => {
    if (thread.title === 'New Chat') {
      const preview = getFirstMessagePreview(thread.id);
      if (preview) return preview;
    }
    return thread.title;
  };

  const renderThreadItem = (thread: Thread) => {
    const tenantColor = getTenantColor(thread.tenantId);
    const tooltip = getThreadTooltip(thread);
    const isHovered = hoveredThreadId === thread.id;

    return (
      <div
        key={thread.id}
        className={`thread-item ${thread.id === threadId ? 'active' : ''}`}
        style={{ borderLeftColor: tenantColor }}
        onClick={() => handleSwitchThread(thread.id)}
        onMouseEnter={() => setHoveredThreadId(thread.id)}
        onMouseLeave={() => setHoveredThreadId(null)}
      >
        <div className="thread-icon">
          <MessageSquare size={16} />
        </div>
        <div className="thread-content">
          {editingThreadId === thread.id ? (
            <input
              type="text"
              value={editingTitle}
              onChange={(e) => setEditingTitle(e.target.value)}
              onBlur={() => handleSaveTitle(thread.id)}
              onKeyDown={(e) => handleKeyPress(e, thread.id)}
              className="thread-title-input"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="thread-title">{getDisplayTitle(thread)}</div>
          )}
          {/* Tooltip on hover */}
          {tooltip && isHovered && (
            <div className="thread-context-tooltip">
              {tooltip}
            </div>
          )}
        </div>
        <div className="thread-actions">
          <button
            className="thread-action-btn"
            onClick={(e) => handleStartEditing(thread, e)}
            title="Rename"
          >
            <Edit3 size={14} />
          </button>
          <button
            className="thread-action-btn delete"
            onClick={(e) => handleDeleteThread(thread.id, e)}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      {/* Logo Section */}
      <div className="sidebar-header">
        <div className="logo-section">
          <img src={logo} alt="Nomi" className="logo-icon" />
        </div>
      </div>

      {/* Workspace Selectors - Under logo, above New Chat */}
      <div className="selector-section">
        {!isSingleTenant && availableTenants.length > 1 ? (
          <TenantSelector />
        ) : (
          /* Single-tenant or only one tenant available: show static badge */
          currentTenant && (
            <div className="single-tenant-badge">
              <span className="single-tenant-name">{currentTenant.name}</span>
            </div>
          )
        )}
        <ProjectSelector />
      </div>

      {/* Action Buttons - New Chat + Search side by side */}
      <div className="action-buttons-row">
        <button className="new-chat-btn" onClick={onNewThread}>
          <Plus size={18} />
          <span>New chat</span>
        </button>
        <button
          className="search-btn"
          onClick={() => setIsSearching(!isSearching)}
          title="Search conversations"
        >
          <Search size={18} />
        </button>
      </div>

      {/* Search Input (expandable) */}
      {isSearching && (
        <div className="search-section">
          <div className="search-input-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              autoFocus
            />
            <button
              className="search-close-btn"
              onClick={() => {
                setIsSearching(false);
                setSearchQuery('');
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Conversations Section */}
      <div className="conversations-section">
        <div className="conversations-header">
          <span>Your conversations</span>
          {threads.length > 0 && (
            <button
              className="clear-all-btn"
              onClick={handleClearAllThreads}
              title="Clear all conversations"
            >
              Clear All
            </button>
          )}
        </div>

        <div className="thread-list">
          {threads.length === 0 ? (
            <div className="no-threads">
              <MessageSquare size={24} />
              <p>No conversations yet</p>
              <span>Start a new chat to begin</span>
            </div>
          ) : nonEmptyGroups.length === 0 && searchQuery ? (
            <div className="no-threads">
              <Search size={24} />
              <p>No results found</p>
              <span>Try a different search term</span>
            </div>
          ) : (
            nonEmptyGroups.map(group => (
              <div key={group} className="thread-group">
                <div className="thread-group-label">{getGroupLabel(group)}</div>
                {groupedThreads[group].map(renderThreadItem)}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer - Sign Out */}
      <div className="sidebar-footer">
        {onLogout && (
          <button className="logout-btn" onClick={onLogout}>
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
